import { useCallback, useEffect, useRef, useState } from "react";
import "./fonts/fonts.css";
import "./index.css";
import { NAME, INTRO, EMAIL, LINKEDIN, WORK } from "./content";
import Mark from "./Mark";
import Develop from "./Develop";

// ───────────────────────────────────────────────────────────────
// The site — one column, one type size, black and white.
//
// Color is rationed: it only appears while you look at a piece of
// work. Hover a row and the mark takes that project's color, the
// row's index picks it up, and the preview develops from a 1-bit
// dither into full color. Leave, and it all returns to ink.
// ───────────────────────────────────────────────────────────────

const INK = "#ededeb";
const PAPER = "#0c0c0c";

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

const luminance = (hex) => {
  const [r, g, b] = hex.match(/\w\w/g).map((x) => parseInt(x, 16) / 255);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

// ───────────────────────────────────────────────────────────────
// Preview — on wide screens it sits in the open space right of the
// column and glides vertically to the row you're on, so it never
// covers the list. Narrower, it trails just below the cursor.
// No tilt, no glass: a plain frame, so the only event is the color
// developing inside.
// ───────────────────────────────────────────────────────────────

const GAP = 64;

function layout() {
  const col = document.querySelector(".col");
  const right = col ? col.getBoundingClientRect().right - 24 : 0; // minus padding
  const room = window.innerWidth - right - GAP - 48;
  return room >= 280 ? { side: true, x: right + GAP, w: Math.min(room, 480) } : { side: false, w: 320 };
}

function Preview({ item: live, onEdge }) {
  const ref = useRef(null);
  const mouse = useRef(null);
  const pos = useRef(null);
  // measured after mount — the column doesn't exist during first render
  const [box, setBox] = useState({ side: false, w: 320 });
  // hold the last item through the fade-out, then let it unmount
  const [item, setItem] = useState(live);
  useEffect(() => {
    if (live) {
      setItem(live);
      return undefined;
    }
    const t = setTimeout(() => setItem(null), 250);
    return () => clearTimeout(t);
  }, [live]);

  useEffect(() => {
    const onMove = (e) => {
      mouse.current = { x: e.clientX, y: e.clientY };
    };
    const onResize = () => setBox(layout());
    onResize();
    window.addEventListener("mousemove", onMove);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  useEffect(() => {
    if (!item) {
      pos.current = null;
      return undefined;
    }
    let raf;
    const tick = () => {
      const el = ref.current;
      const m = mouse.current;
      if (el && m) {
        const h = box.w / item.ratio;
        const tx = box.side ? box.x : clamp(m.x - 60, 16, window.innerWidth - box.w - 16);
        const ty = box.side
          ? clamp(m.y - h / 2, 24, window.innerHeight - h - 24)
          : m.y + 28 + h < window.innerHeight - 16
          ? m.y + 28
          : m.y - 28 - h;
        if (!pos.current) pos.current = { x: tx, y: ty };
        pos.current.x += (tx - pos.current.x) * 0.14;
        pos.current.y += (ty - pos.current.y) * 0.14;
        el.style.transform = `translate(${pos.current.x}px, ${pos.current.y}px)`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [item, box]);

  return (
    <div ref={ref} className="preview" aria-hidden style={{ width: box.w, opacity: live ? 1 : 0 }}>
      {item && <Develop key={item.media} src={item.media} ratio={item.ratio} onEdge={(hex) => onEdge(item.media, hex)} />}
    </div>
  );
}

export default function App() {
  const isTouch = useRef(window.matchMedia("(hover: none), (pointer: coarse)").matches).current;
  const [active, setActive] = useState(null); // index into WORK

  // Esc closes an open (tapped) preview
  useEffect(() => {
    if (active === null) return undefined;
    const onKey = (e) => e.key === "Escape" && setActive(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active]);

  const item = active !== null ? WORK[active] : null;

  // The page takes on the open preview's edge color, so the frame
  // dissolves into it. Only when that edge reads as paper (dark);
  // otherwise the page just leans slightly toward the project color.
  const [edges, setEdges] = useState({});
  const onEdge = useCallback((media, hex) => setEdges((e) => (e[media] === hex ? e : { ...e, [media]: hex })), []);
  let wash = PAPER;
  if (item) {
    const edge = edges[item.media];
    const l = edge ? luminance(edge) : null;
    wash = edge && l < 0.16 ? edge : `color-mix(in srgb, ${item.color} 14%, ${PAPER})`;
  }

  return (
    <main className="page" style={{ "--ink": INK, "--paper": wash, "--hue": item ? item.color : INK }}>
      <div className="col">
        <header className="rise" style={{ "--d": 0 }}>
          <Mark ink={INK} paper="rgba(0, 0, 0, 0)" color={item && item.color} route={item && item.route} />
        </header>

        <section className="intro rise" style={{ "--d": 1 }}>
          <h1>{NAME}</h1>
          {INTRO.map((line, i) => (
            <p key={i} className="muted">
              {line.map((part, j) =>
                typeof part === "string" ? (
                  part
                ) : (
                  <a key={j} href={part.href} target="_blank" rel="noreferrer">
                    {part.text}
                  </a>
                )
              )}
            </p>
          ))}
        </section>

        <section className="rise" style={{ "--d": 2 }}>
          <h2 className="muted label">Selected work</h2>
          <ol className={`work${active !== null ? " has-active" : ""}`} onMouseLeave={isTouch ? undefined : () => setActive(null)}>
            {WORK.map((w, i) => (
              <li key={w.title} className={active === i ? "is-active" : undefined}>
                <button
                  type="button"
                  className="row"
                  aria-expanded={isTouch ? active === i : undefined}
                  onMouseEnter={isTouch ? undefined : () => setActive(i)}
                  onFocus={isTouch ? undefined : () => setActive(i)}
                  onBlur={isTouch ? undefined : () => setActive(null)}
                  onClick={isTouch ? () => setActive((a) => (a === i ? null : i)) : undefined}
                  style={{ "--c": w.color }}
                >
                  <span className="n">{String(i + 1).padStart(2, "0")}</span>
                  <span className="t">{w.title}</span>
                  <span className="o muted">{w.org}</span>
                  <span className="y muted">{w.year}</span>
                </button>
                {/* touch has no hover: the preview opens inline under the row */}
                {isTouch && active === i && (
                  <div className="inline-preview">
                    <Develop src={w.media} ratio={w.ratio} onEdge={(hex) => onEdge(w.media, hex)} />
                  </div>
                )}
              </li>
            ))}
          </ol>
        </section>

        <footer className="rise" style={{ "--d": 3 }}>
          <a href={`mailto:${EMAIL}`}>Email</a>
          <a href={LINKEDIN} target="_blank" rel="noreferrer">
            LinkedIn
          </a>
        </footer>
      </div>

      {!isTouch && <Preview item={item} onEdge={onEdge} />}
    </main>
  );
}
